import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiGallection } from '@wix/da-papi-types';
import {
  BiData,
  BiContextProvidedValue,
  DefaultBiEvent,
  MoreMenuClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { gallectionTypeToResourceType } from '@wix/da-shared-react/pkg/types/gallection';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import IconButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import Ellipsis from '@wix/da-ds/pkg/Icons/Ellipsis';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';

export interface Props {
  className?: string;
  folder: PapiGallection;
  sizeOverride?: 'small' | 'medium' | 'large';
  onBookmarkRemove: () => void;
}

const BookmarkActionsDropdown: FC<Props> = ({
  className,
  folder,
  sizeOverride,
  onBookmarkRemove,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const dropdownItems: NullableDropdownItem[] = [
    {
      label: t('studio.header.bookmarkActions.remove'),
      onClick: onBookmarkRemove,
      biData: BiData<DefaultBiEvent>({
        evid: 2,
        typeid: gallectionTypeToResourceType(folder.type),
        itemid: folder.folderId,
        sectionname: BiContextProvidedValue,
        click_info: 'remove_shortcut',
      }),
    },
  ];

  const size = sizeOverride || (isMobile ? 'medium' : 'small');
  const iconSize = {
    small: IconSize.SMALL,
    medium: IconSize.MEDIUM,
    large: IconSize.LARGE,
  }[size];

  /* note: wrapper div avoids layout shift because button component might render an extra div on ssr */
  return (
    <div className={className} onClick={e => e.preventDefault()}>
      <IconButtonWithContextMenu
        icon={Ellipsis}
        iconSizeOverride={iconSize}
        size={size}
        aria-label={t('common.moreActions')}
        items={dropdownItems}
        biData={BiData<MoreMenuClickBiEvent>({
          evid: 417,
          typeid: ResourceType.STASH_FOLDER,
          itemid: folder.folderId,
        })}
        stopPropagation
        noPaddings
      />
    </div>
  );
};
BookmarkActionsDropdown.displayName = 'BookmarkActionsDropdown';

export default BookmarkActionsDropdown;
